/* eslint-disable complexity */
import { AxiosResponse } from 'axios';

import { TestStep } from '@qality/common-model';
import {
  StorageOfUniqueServices,
  AxiosWrapperService as axios,
} from '@qality/common-service';
import { Utils } from '@qality/common-utils';

const TEST_CASE_NAME = 'TestCase.Name';
const STEP = 'TestStep.Step';
const DATA = 'TestStep.Data';
const RESULT = 'TestStep.Result';

type MappingItem = {
  from: {
    label?: string;
    value?: string;
    example?: string;
  };
  to?: {
    label?: string;
    value?: string;
    type?: string;
    requird: boolean;
  };
};

type TestCase = {
  testSteps: Array<TestStep>;
  name?: string;
};

type TestCaseData = {
  IssueKey: string;
  TestCaseName: string;
  Description: string;
  TestStep: string;
  TestData: string;
  TestResult: string;
};

export default {
  async importTestCases(
    projectId: string,
    mapping: Array<MappingItem>,
    data: Array<TestCaseData>,
    qalityTestFields: any,
    delimiter: string
  ): Promise<AxiosResponse> {
    const APIConstants = StorageOfUniqueServices.get('APIConstants');
    const AuthenticateService = StorageOfUniqueServices.get(
      'AuthenticateService'
    );
    const WindowOriginService = StorageOfUniqueServices.get(
      'WindowOriginService'
    );
    const url = await AuthenticateService.authenticateByJWT(
      WindowOriginService.origin + APIConstants.URL_IMPORT_TEST_CASES
    );
    const fieldsMap = mapArrayOfFieldsToMap(qalityTestFields);
    const requestData = mapDataToRequestData(
      projectId,
      mapping,
      data,
      fieldsMap,
      delimiter
    );
    return await axios.post(url, requestData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },
};

function mapDataToRequestData(
  projectId: number,
  mapping: Array<MappingItem>,
  data: Array<TestCaseData>,
  fieldsMap,
  delimiter: string
): { projectId: number; testCases: Array<TestCase> } {
  const testCases = {};

  const mappingForTestCaseName = getMappingForTestCaseName(mapping);

  data.forEach((row) => {
    const testCaseName = row[mappingForTestCaseName.from.value];
    if (Utils.isNilOrEmpty(testCases[testCaseName])) {
      testCases[testCaseName] = createTestCase(
        row,
        mapping,
        fieldsMap,
        delimiter
      );
    } else {
      updateTestCase(
        testCases[testCaseName],
        row,
        mapping,
        fieldsMap,
        delimiter
      );
    }
  });

  return {
    projectId,
    testCases: Object.values(testCases),
  };
}

function updateTestCase(
  testCase: TestCase,
  row: TestCaseData,
  mapping: Array<MappingItem>,
  fieldsMap: any,
  delimiter: string
): void {
  const testStep: TestStep = {};

  mapping.forEach((mappingItem) => {
    if (mappingItem.to) {
      switch (mappingItem.to.value) {
        case TEST_CASE_NAME:
          if (!Utils.isNilOrEmpty(row[mappingItem.from.value])) {
            testCase.name = row[mappingItem.from.value];
          }
          break;
        case STEP:
          testStep.step = row[mappingItem.from.value];
          break;
        case DATA:
          testStep.data = row[mappingItem.from.value];
          break;
        case RESULT:
          testStep.result = row[mappingItem.from.value];
          break;
        default:
          if (!Utils.isNilOrEmpty(row[mappingItem.from.value])) {
            testCase[mappingItem.to.value] = StorageOfUniqueServices.get(
              'TestCaseFieldMapper'
            ).mapValue(mappingItem, row, fieldsMap, delimiter);
          }
      }
    }
  });

  testCase.testSteps.push(testStep);
}

function createTestCase(
  row: TestCaseData,
  mapping: Array<MappingItem>,
  qalityTestFields: any,
  delimiter: string
): { testSteps: Array<TestStep> } {
  const testCase = {
    testSteps: [],
  };
  updateTestCase(testCase, row, mapping, qalityTestFields, delimiter);
  return testCase;
}

function getMappingForTestCaseName(mapping: Array<MappingItem>) {
  return mapping.find(
    (mappingItem) => mappingItem.to?.value === TEST_CASE_NAME
  );
}

function mapArrayOfFieldsToMap(qalityTestFields): {
  [key: string]: any;
} {
  const fieldsMap = {};

  qalityTestFields.forEach((field) => {
    fieldsMap[field.key] = field;
  });

  return fieldsMap;
}
